import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePreferences } from '../../hooks/usePreferences';
import { HealthSummary } from './HealthSummary';
import { HealthExpanded } from './HealthExpanded';
import './ActionBarHealth.css';

interface ServiceHealth {
  name: string;
  port: number;
  status: 'online' | 'offline' | 'degraded';
  responseTime?: number;
  error?: string;
  metrics?: Record<string, unknown>;
}

interface EnvironmentHealth {
  backend: ServiceHealth;
  frontend: ServiceHealth;
  services: {
    tradovate: ServiceHealth;
    cmeMarketHours: ServiceHealth;
    accountStats: ServiceHealth;
    news: ServiceHealth;
  };
}

export interface HealthResponse {
  staging: EnvironmentHealth;
  production: EnvironmentHealth;
  timestamp: number;
}

export const ActionBarHealth: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [healthData, setHealthData] = useState<HealthResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { preferences, updatePreference } = usePreferences();
  const isPinned = preferences?.actionBarHealth?.isPinned ?? false;

  const containerRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const fetchHealth = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const isLocalhost = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1';
      const isStaging = window.location.port === '4002' ||
                        window.location.hostname.includes('staging');

      let baseUrl: string;
      if (isLocalhost) {
        baseUrl = isStaging ? 'http://localhost:9647' : 'http://localhost:9547';
      } else {
        baseUrl = isStaging
          ? 'https://staging-uttapi.ginuga.com'
          : 'https://uttapi.ginuga.com';
      }

      const response = await fetch(`${baseUrl}/api/health/all`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setHealthData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on mount and when expanded
  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    if (isExpanded) {
      fetchHealth();
    }
  }, [isExpanded, fetchHealth]);

  // Toggle handlers
  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleTogglePin = useCallback(() => {
    updatePreference('actionBarHealth', { isPinned: !isPinned });
  }, [isPinned, updatePreference]);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  // Click outside to close (when not pinned)
  useEffect(() => {
    if (!isExpanded || isPinned) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideExpanded = expandedRef.current && !expandedRef.current.contains(target);

      if (isOutsideContainer && isOutsideExpanded) {
        setIsExpanded(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, isPinned]);

  // Position the dropdown.
  //
  // The old rule was `Math.max(8, rect.left - 150)`, which clamps the LEFT edge
  // and never the right. The panel is 320px wide, so on a 390px phone with the
  // health button over toward the right it landed at 150 and ran ~80px off the
  // screen with no way to scroll to it.
  //
  // Narrow screens centre it instead of anchoring: at these widths the panel is
  // most of the viewport, so "under the button" is a distinction without a
  // difference and centring is what reads as deliberate. Wider screens keep the
  // anchored behaviour but gain the right-edge clamp the old rule was missing.
  useEffect(() => {
    if (!isExpanded || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewport = window.innerWidth;
    const MARGIN = 8;
    // Mirrors `width: min(320px, calc(100vw - 16px))` in the stylesheet. Kept in
    // step with it deliberately — measuring the rendered panel would need a
    // second layout pass to avoid a visible jump on open.
    const panelWidth = Math.min(320, viewport - MARGIN * 2);

    const left =
      viewport <= 768
        ? Math.max(MARGIN, (viewport - panelWidth) / 2)
        : Math.min(
            Math.max(MARGIN, rect.left - 150),
            viewport - panelWidth - MARGIN
          );

    setDropdownPosition({ top: rect.bottom + 4, left });
  }, [isExpanded]);

  // Calculate summary stats
  const getStatusCounts = () => {
    if (!healthData) return { online: 0, total: 0 };

    const isStaging = window.location.port === '4002' ||
                      window.location.hostname.includes('staging');
    const env = isStaging ? healthData.staging : healthData.production;

    if (!env) return { online: 0, total: 0 };

    const services = [
      env.backend,
      env.frontend,
      env.services.tradovate,
      env.services.cmeMarketHours,
      env.services.accountStats,
      env.services.news,
    ];

    return {
      online: services.filter(s => s.status === 'online').length,
      total: services.length,
    };
  };

  const { online, total } = getStatusCounts();

  return (
    <div ref={containerRef} className="action-bar-health">
      <HealthSummary
        online={online}
        total={total}
        isLoading={isLoading}
        hasError={!!error}
        isExpanded={isExpanded}
        onClick={handleToggleExpand}
        onRefresh={fetchHealth}
      />

      {isExpanded && createPortal(
        <div
          ref={expandedRef}
          className="abh-expanded-portal"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 1000,
          }}
        >
          <HealthExpanded
            healthData={healthData}
            isLoading={isLoading}
            error={error}
            isPinned={isPinned}
            onTogglePin={handleTogglePin}
            onClose={handleClose}
            onRefresh={fetchHealth}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionBarHealth;
